/**
 * DashboardNavBar
 *
 * Renders navigation so users are never "trapped" inside the venue dashboard.
 *
 * Mobile  (< 768 px) : fixed bottom bar — Map | My Venues | Profile | Language
 * Desktop (≥ 768 px) : fixed top-right strip that visually merges with the
 *                      orange header — icons + burger-style language picker
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'EN', full: 'English',      flag: '🇬🇧' },
  { code: 'th', label: 'TH', full: 'ภาษาไทย',     flag: '🇹🇭' },
  { code: 'vi', label: 'VI', full: 'Tiếng Việt',   flag: '🇻🇳' },
  { code: 'fr', label: 'FR', full: 'Français',      flag: '🇫🇷' },
  { code: 'ru', label: 'RU', full: 'Русский',       flag: '🇷🇺' },
  { code: 'de', label: 'DE', full: 'Deutsch',       flag: '🇩🇪' },
];

function useMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

function applyLang(i18n, code) {
  i18n.changeLanguage(code);
  document.documentElement.lang = code;
  document.body.className = document.body.className.replace(/\blang-\w+\b/g, '').trim();
  if (code === 'th') document.body.classList.add('lang-th');
  localStorage.setItem('hl_locale', code);
}

// ─── language picker (shared, direction-aware) ────────────────────────────────

function LangPicker({ openUpward = false, onClose }) {
  const { i18n } = useTranslation();
  return (
    <div style={{
      position: 'absolute',
      [openUpward ? 'bottom' : 'top']: 'calc(100% + 2px)',
      right: 0,
      background: '#1a1a2e',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: openUpward ? '10px 10px 0 0' : 10,
      overflow: 'hidden',
      zIndex: 310,
      minWidth: 180,
      boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
    }}>
      {LANGUAGES.map(lang => {
        const active = lang.code === i18n.language;
        return (
          <button
            key={lang.code}
            onClick={() => { applyLang(i18n, lang.code); onClose(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '10px 14px',
              background: active ? 'rgba(249,115,22,0.2)' : 'none',
              border: 'none', cursor: 'pointer',
              color: active ? '#fb923c' : '#e2e8f0',
              fontSize: 14, textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 20 }}>{lang.flag}</span>
            <span style={{ fontWeight: active ? 600 : 400 }}>{lang.full}</span>
            {active && <span style={{ marginLeft: 'auto', color: '#fb923c' }}>✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── mobile bottom nav ────────────────────────────────────────────────────────

function MobileNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const isActive = (path) => location.pathname.startsWith(path);

  const tab = (icon, label, path) => {
    const active = isActive(path);
    return (
      <button
        key={path}
        onClick={() => navigate(path)}
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 3, background: 'none', border: 'none',
          cursor: 'pointer', padding: '8px 0',
          color: active ? '#fb923c' : 'rgba(255,255,255,0.55)',
        }}
      >
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: active ? 700 : 400, letterSpacing: '0.03em' }}>{label}</span>
      </button>
    );
  };

  return (
    <>
      {/* spacer so page content isn't hidden under nav */}
      <div style={{ height: 64 }} />

      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, height: 64,
        background: '#111827',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'stretch',
        zIndex: 300,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}>
        {tab('🗺️', 'Map', '/')}
        {tab('🏠', 'My Venues', '/venue-dashboard')}
        {tab('👤', 'Profile', '/profile')}

        {/* Language tab */}
        <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
          <button
            onClick={() => setLangOpen(o => !o)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 3, background: 'none', border: 'none',
              cursor: 'pointer', padding: '8px 0',
              color: langOpen ? '#fb923c' : 'rgba(255,255,255,0.55)',
            }}
          >
            <span style={{ fontSize: 20 }}>{currentLang.flag}</span>
            <span style={{ fontSize: 10, fontWeight: 400, letterSpacing: '0.03em' }}>{currentLang.label}</span>
          </button>

          {langOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 309 }} onClick={() => setLangOpen(false)} />
              <LangPicker openUpward onClose={() => setLangOpen(false)} />
            </>
          )}
        </div>
      </nav>
    </>
  );
}

// ─── desktop top-right strip ──────────────────────────────────────────────────

function DesktopNav() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const iconBtn = (content, onClick, title) => (
    <button
      title={title}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36,
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 8, cursor: 'pointer',
        color: 'white', fontSize: 16,
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
    >
      {content}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0,
      height: 64,
      display: 'flex', alignItems: 'center',
      gap: 8, paddingRight: 16,
      zIndex: 200,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    }}>
      {/* Map */}
      {iconBtn('🗺️', () => navigate('/'), 'Back to map')}

      {/* My Venues */}
      {iconBtn('🏠', () => navigate('/venue-dashboard'), 'My venues')}

      {/* Profile */}
      {iconBtn('👤', () => navigate('/profile'), 'Profile')}

      {/* Language */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setLangOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            height: 36, padding: '0 10px',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 8, cursor: 'pointer',
            color: 'white', fontSize: 13, fontWeight: 600,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
        >
          <span style={{ fontSize: 18 }}>{currentLang.flag}</span>
          <span>{currentLang.label}</span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
        </button>

        {langOpen && (
          <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 209 }} onClick={() => setLangOpen(false)} />
            <LangPicker openUpward={false} onClose={() => setLangOpen(false)} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── exported component ───────────────────────────────────────────────────────

export default function DashboardNavBar() {
  const mobile = useMobile();
  return mobile ? <MobileNav /> : <DesktopNav />;
}
